import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Star, MapPin, Phone, Shield, Clock } from 'lucide-react';
import { HiOutlineGlobeAlt } from 'react-icons/hi';
import { FiHome } from 'react-icons/fi';
import { IoAirplaneOutline } from 'react-icons/io5';
import { MdOutlineFlight, MdHome, MdOutlineExplore } from 'react-icons/md';
import {
  buildBookingDataFromVehicle,
  fetchPublicVehicles,
  groupVehiclesByTrip,
  toBookingTripLabel,
} from '../services/vehicleService';

// Import vehicle images (adjust paths as needed)
import sedan from "../assets/Swift1.png";
import ertiga from "../assets/front-left-side-47.avif";
import crysta from "../assets/images.jpg";
import hycross from "../assets/hycross.avif";
import fortuner from "../assets/fortunes.avif";
import tempo from "../assets/tempo.jpg";
import Urbania from "../assets/Urbania8.png";
import InnovaBase from "../assets/innovabase.avif";
import city from "../assets/etios-car.jpg";

const Y = "#FFD600";
const YD = "#B8860B";
const YL = "#FFF9E6";
const font = "'Nunito', sans-serif";
const fontD = "'Barlow', sans-serif";
const KIA_CARENS_IMAGE_URL = "https://cdn-s3.autocarindia.com/legacy/cdni/mmv_images/colors/20250808114824_Kia_Carens_Sparkling_Silver[1].png?w=728&q=75&fm=auto";
const KIA_CARNIVAL_IMAGE_URL = "https://cdn.carhp.in/kia/kia_carnival_glacier_white_pearl.jpg?format=webp&width=800&q=75";
const VOLVO_BUS_IMAGE_URL = "https://cdn.motorbazee.com/ImageData/model/800x600/195525.jpg";

const VEHICLES = {
  outstation: [
    { id:2, image:"sedan", name:"Swift Dzire (4+1 seats)", models:"Swift Dzire", type:"Comfort", seaters:"4", bags:2, price:"₹12/km", per:null, tag:"Popular", badge:"popular", rating:4.7, features:["Spacious Boot","Business Travel","Smooth Ride"] },
    { id:10, image:"premiumSedan", name:"Etios (4+1 seats)", models:" Etios", type:"Comfort", seaters:"4", bags:2, price:"₹12/km", per:null, tag:"Popular", badge:"popular", rating:4.7, features:["Spacious Boot","Business Travel","Smooth Ride"] },
    { id:4, image:"ertiga", name:"Ertiga (6+1 seats)", models:"Ertiga, Innova", type:"Family", seaters:"6", bags:4, price:"₹15/km", per:null, tag:"Best Value", badge:"value", rating:4.6, features:["Group Travel","Extra Luggage","3 Rows"] },
    { id:5, image:"crysta", name:"Innova Crysta (7+1 seats)", models:"Toyota Innova", type:"Premium SUV", seaters:"7", bags:5, price:"₹19/km", per:null, tag:null, badge:null, rating:4.9, features:["Luxury Interior","Captain Seats","Premium AC"] },
    { id:6, image:"hycross", name:"Innova Hybrid (7+1 seats)", models:"Innova Hycross", type:"Luxury", seaters:"7", bags:5, price:"₹23/km", per:null, tag:"Top Pick", badge:"top", rating:5.0, features:["Hybrid Engine","Panoramic Roof","ADAS Safety"] },
    { id:7, image:"fortuner", name:"Fortuner (7+1 seats)", models:"Fortuner", type:"Luxury", seaters:"7", bags:5, price:"₹60/km", per:null, tag:"Top Pick", badge:"top", rating:5.0, features:["Hybrid Engine","Panoramic Roof","ADAS Safety"] },
    { id:8, image:"tempo", name:"Tempo Traveller", models:"9 / 12 Seater", type:"Group", seaters:12, bags:8, price:"₹24/km", per:null, tag:null, badge:null, rating:4.5, features:["Pushback Seats","Group Trips","Large Luggage"] },
    { id:9, image:"Urbania", name:"Urbania", models:"9–16 Seater", type:"Large Group", seaters:"16", bags:20, price:"₹43/km", per:null, tag:null, badge:null, rating:4.4, features:["Restroom","Luggage Bay","Events & Tours"] },
    { id:11, image:"buss", name:"Bus", models:"21-50 Seater", type:"Large Group", seaters:"50", bags:20, price:"₹35/km", per:null, tag:null, badge:null, rating:4.4, features:["Restroom","Luggage Bay","Events & Tours"] },
  ],
  airport: [
    { id:10, image:"sedan", name:"Swift Dzire (4+1 seats)", models:"Swift Dzire, Etios", type:"Comfort", seaters:"4+1", bags:2, price:"₹999", per:null, tag:"Most Booked", badge:"popular", rating:4.8, features:["Flight Tracking","Meet & Greet","Waiting Included"] },
    { id:11, image:"premiumSedan", name:"Etios (4+1 seats)", models:"Swift Dzire, Etios", type:"Comfort", seaters:"4+1", bags:2, price:"₹999", per:null, tag:"Most Booked", badge:"popular", rating:4.8, features:["Flight Tracking","Meet & Greet","Waiting Included"] },
    { id:12, image:"ertiga", name:"Ertiga (6+1 seats)", models:"Innova, Ertiga", type:"Family", seaters:"6+1", bags:4, price:"₹1,599", per:null, tag:null, badge:null, rating:4.7, features:["Extra Luggage","Family Comfort","Premium Service"] },
    // { id:18, image:"kiaCarens", name:"Kia Carens (6+1 seats)", models:"Kia Carens", type:"Family", seaters:"6+1", bags:5, price:"₹1,699", per:null, tag:null, badge:null, rating:4.7, features:["Extra Luggage","Family Comfort","Premium Service"] },
    { id:14, image:"crysta", name:"Innova Crysta (7+1 seats)", models:"Toyota Innova Crysta", type:"Premium Family", seaters:7, bags:5, price:"₹1,999", per:null, tag:null, badge:null, rating:4.8, features:["Luxury Interior","Captain Seats","Premium AC"] },
    { id:15, image:"hycross", name:"Innova Hycross (7+1 seats)", models:"Toyota Innova Crysta", type:"Premium Family", seaters:7, bags:5, price:"₹2,299", per:null, tag:null, badge:null, rating:4.8, features:["Luxury Interior","Captain Seats","Premium AC"] },
    { id:16, image:"fortuner", name:"Fortuner (7+1 seats)", models:"Toyota Fortuner", type:"Premium SUV", seaters:7, bags:5, price:"₹4,000", per:null, tag:null, badge:null, rating:4.8, features:["Luxury Interior","Captain Seats","Premium AC"] },
    { id:17, image:"tempo", name:"Tempo Traveller", models:"12 Seater", type:"Group", seaters:12, bags:8, price:"₹4,500", per:null, tag:null, badge:null, rating:4.7, features:["Extra Luggage","Group Travel","Premium Service"] },
    { id:18, image:"Urbania", name:"Urbania", models:"12-13 Seater", type:"Group", seaters:13, bags:8, price:"₹8,000", per:null, tag:"Group Special", badge:"value", rating:4.6, features:["Extra Luggage","Group Travel","Premium Service"] },
    { id:19, image:"buss", name:"Bus", models:"21-50 Seater", type:"Large Group", seaters:50, bags:20, price:"₹10,000", per:null, tag:"Group Special", badge:"value", rating:4.6, features:["Extra Luggage","Group Travel","Premium Service"] },
  ],
  local: [
    { id:13, image:"sedan", name:"Swift Dzire", models:"Swift Dzire, Etios", type:"Comfort 4hr", seaters:4, bags:2, price:"₹1,300", per:"/4 hrs (40 kms)", tag:"Popular", badge:"popular", rating:4.6, features:["40 kms included","Business Meetings","Smooth Ride"] },
    { id:21, image:"premiumSedan", name:"Etios (4+1 seats)", models:" Etios", type:"Comfort 4hr", seaters:4, bags:2, price:"₹2,000", per:"/4 hrs (40 kms)", tag:"Popular", badge:"popular", rating:4.6, features:["40 kms included","Business Meetings","Smooth Ride"] },
    { id:17, image:"ertiga", name:"Ertiga (6+1 seats)", models:"Maruti Ertiga", type:"Family 8hr", seaters:7, bags:4, price:"₹1,600", per:"/8 hrs (80 kms)", tag:"Best Value", badge:"value", rating:4.5, features:["80 kms included","7 Seater","Toll & Parking Extra"] },
    { id:18, image:"fortuner", name:"Fortuner & Kia Carnival (7+1 seats)", models:"Toyota Innova", type:"Premium 8hr", seaters:7, bags:5, price:"₹3,500", per:"/8 hrs (80 kms)", tag:"Popular", badge:"popular", rating:4.8, features:["80 kms included","7 Seater","Spacious Interior","Toll & Parking Extra"] },
    { id:19, image:"crysta", name:"Innova Crysta (7+1 seats)", models:"Toyota Innova Crysta", type:"Luxury 8hr", seaters:7, bags:5, price:"₹4,000", per:"/8 hrs (80 kms)", tag:"Premium", badge:"top", rating:4.9, features:["80 kms included","7 Seater","Luxury Interior","Toll & Parking Extra"] },
    { id:14, image:"hycross", name:"Innova Hycross Hybrid(7+1 seats)", models:"Xylo, Innova", type:"Family 4hr", seaters:7, bags:4, price:"₹1,800", per:"/4 hrs (40 kms)", tag:null, badge:null, rating:4.5, features:["40 kms included","Group Outings","Family Trips"] },
    { id:20, image:"tempo", name:"Tempo Traveller", models:"12-13 Seater", type:"Group 8hr", seaters:13, bags:8, price:"₹5,000", per:"/8 hrs (80 kms)", tag:"Group Special", badge:"value", rating:4.6, features:["80 kms included","13 Seater","Pushback Seats","Toll & Parking Extra"] },
    { id:22, image:"Urbania", name:"Urbania", models:"12-13 Seater", type:"Group 8hr", seaters:13, bags:8, price:"₹8,000", per:"/8 hrs (80 kms)", tag:"Group Special", badge:"value", rating:4.6, features:["80 kms included","13 Seater","Pushback Seats","Toll & Parking Extra"] },
    { id:23, image:"buss", name:"Bus", models:"12-13 Seater", type:"Group 8hr", seaters:13, bags:8, price:"₹10,000", per:"/8 hrs (80 kms)", tag:"Group Special", badge:"value", rating:4.6, features:["80 kms included","13 Seater","Pushback Seats","Toll & Parking Extra"] },
  ]
};

const TRIPS = [
  { id:"airport", icon: IoAirplaneOutline, emojiIcon: MdOutlineFlight, label:"Airport", sub:"Transfer", stats:["9 Vehicles","₹999"], color:"#1A73E8", desc:"Flight pickups & drops" },
  { id:"outstation", icon: HiOutlineGlobeAlt, emojiIcon: MdOutlineExplore, label:"Outstation", sub:"Inter-City", stats:["8 Vehicles","₹8/km"], color:"#FF6B35", desc:"Long distance travel" },
  { id:"local", icon: FiHome, emojiIcon: MdHome, label:"Local", sub:"Hourly", stats:["6 Packages","₹500/4hrs"], color:"#0B8A4D", desc:"City errands & tours" },
];

const BADGES = {
  popular: { bg:"#FFF3CD", color:"#92400E", label:"🔥 Popular" },
  value:   { bg:"#D1FAE5", color:"#065F46", label:"💎 Best Value" },
  top:     { bg:"#FEE2E2", color:"#991B1B", label:"⭐ Top Pick" },
};

// Vehicle image mapping
const getVehicleImage = (vehicle) => {
  const map = { 
    sedan: sedan,
    premiumSedan: city,
    ertiga: ertiga,
    crysta: crysta,
    hycross: hycross,
    fortuner: fortuner,
    tempo: tempo,
    Urbania: Urbania,
    buss: VOLVO_BUS_IMAGE_URL,
    bus: VOLVO_BUS_IMAGE_URL,
    kiaCarens: KIA_CARENS_IMAGE_URL,
    kiaCarnival: KIA_CARNIVAL_IMAGE_URL,
    innova: InnovaBase,
    default: crysta 
  };
  return map[vehicle.image] || map.default;
};

// Mobile Trip Tab Component
function MobileTripTab({ trip, selected, onClick }) {
  const on = selected === trip.id;
  const Icon = trip.icon;

  return (
    <button onClick={onClick} style={{
      flex:1, border:"none", cursor:"pointer",
      background: on ? Y : "#F8F8F8",
      borderRadius:18, padding:"14px 6px 12px",
      transition:"all 0.25s cubic-bezier(0.34,1.56,0.64,1)",
      boxShadow: on ? `0 8px 24px rgba(255,214,0,0.5), 0 2px 8px rgba(0,0,0,0.08)` : "0 1px 3px rgba(0,0,0,0.05)",
      transform: on ? "translateY(-4px) scale(1.04)" : "scale(1)",
      outline:"none",
      position:"relative",
      overflow:"hidden",
      minWidth:0,
    }}>
      {on && (
        <div style={{
          position:"absolute", top:0, left:0, right:0, height:3,
          background:"linear-gradient(90deg, #FF8C00, #FFD600)",
          borderRadius:"3px 3px 0 0",
        }} />
      )}
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:5 }}>
        <div style={{
          width:44, height:44, borderRadius:15,
          background: on ? "rgba(0,0,0,0.1)" : "#fff",
          display:"flex", alignItems:"center", justifyContent:"center",
          boxShadow: on ? "none" : "0 2px 8px rgba(0,0,0,0.08)",
          border: on ? "none" : "1.5px solid #F0F0F0",
        }}>
          <Icon size={20} color={on ? "#1A1A1A" : trip.color} />
        </div>
        <span style={{ fontSize:11, fontWeight:900, color: on ? "#1A1A1A" : "#444", letterSpacing:0.2, maxWidth:"100%", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {trip.label}
        </span>
        <span style={{
          fontSize:8.5, fontWeight:800,
          color: on ? "rgba(0,0,0,0.55)" : "#BBB",
          background: on ? "rgba(0,0,0,0.07)" : "#EFEFEF",
          padding:"2px 8px", borderRadius:20,
          letterSpacing:0.3,
          maxWidth:"100%",
          overflow:"hidden",
          textOverflow:"ellipsis",
          whiteSpace:"nowrap",
        }}>
          {trip.sub}
        </span>
      </div>
    </button>
  );
}



// Mobile Vehicle Card Component
function MobileVCard({ v, onBook, onWhatsApp, index, tripType }) {
  const [pressed, setPressed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('8hr');
  const bc = v.badge ? BADGES[v.badge] : null;
  const imageUrl = getVehicleImage(v);

  // Get extra charges based on vehicle
  const getExtraCharges = (hours) => {
    const vehicleName = v.name?.toLowerCase() || '';
    
    if (vehicleName.includes('etios')) return { extraKm: '15.00', extraHr: '150' };
    if (vehicleName.includes('swift dzire')) return { extraKm: '15.00', extraHr: '150' };
    if (vehicleName.includes('ertiga')) return { extraKm: '20.00', extraHr: '200' };
    if (vehicleName.includes('innova crysta')) return { extraKm: '25.00', extraHr: '250' };
    if (vehicleName.includes('innova hycross')) return { extraKm: '30.00', extraHr: '300' };
    if (vehicleName.includes('fortuner')) return { extraKm: '25.00', extraHr: '250' };
    if (vehicleName.includes('tempo')) return { extraKm: '30.00', extraHr: '300' };
    if (vehicleName.includes('bus')) return { extraKm: '50.00', extraHr: '500' };
    
    return { extraKm: '15.00', extraHr: '150' };
  };

  // Check if vehicle should show only 8hr package
  const showOnly8hr = () => {
    const vehicleName = v.name?.toLowerCase() || '';
    return vehicleName.includes('tempo') || 
           vehicleName.includes('bus') || 
           vehicleName.includes('fortuner') || 
           vehicleName.includes('carnival') || 
           vehicleName.includes('hycross') || 
           vehicleName.includes('crysta');
  };

  // Get display name
  const getDisplayName = () => {
    const name = v.name?.toUpperCase() || '';
    if (name.includes('SEDAN')) return 'SEDAN';
    if (name.includes('ETIOS')) return 'TOYOTA ETIOS';
    if (name.includes('ERTIGA')) return 'MARUTI ERTIGA';
    if (name.includes('CARENS')) return 'KIA CARENS';
    if (name.includes('INNOVA') && name.includes('CRYSTA')) return 'INNOVA CRYSTA';
    if (name.includes('INNOVA') && name.includes('HYBRID')) return 'INNOVA HYBRID';
    if (name.includes('HYCROSS')) return 'INNOVA HYCROSS';
    if (name.includes('FORTUNER')) return 'FORTUNER';
    if (name.includes('TEMPO')) return 'TEMPO TRAVELLER';
    if (name.includes('BUS')) return 'BUS';
    if(name.includes('URBANIA')) return 'URBANIA';
    return v.name?.toUpperCase() || '';
  };

  // Get package details for local trips
  const getPackageDetails = () => {
    if (v.selectedRateCard || v.packageDetails?.baseRate) {
      const details = v.packageDetails || {};
      const key = details.hours === 4 ? '4hr' : '8hr';

      return {
        isSedan: false,
        showOnly8hr: key !== '4hr',
        packages: {
          [key]: {
            label: details.label || details.packageName,
            hours: details.hours ? `${details.hours} Hrs` : '',
            kms: details.kms ? `${details.kms} Kms` : '',
            price: details.price || v.price,
            extraKm: details.extraKm ?? 0,
            extraHr: details.extraHr ?? 0,
          },
        },
      };
    }

    const vehicleName = v.name?.toLowerCase() || '';
    
    if (vehicleName.includes('sedan') || vehicleName.includes('etios')) {
      return {
        isSedan: true,
        showOnly8hr: false,
        packages: {
          '4hr': { hours: '4 Hrs', kms: '40 Kms', price: '₹1,300/-', extraKm: '15.00', extraHr: '160' },
          '8hr': { hours: '8 Hrs', kms: '80 Kms', price: '₹2,200/-', extraKm: '15.00', extraHr: '160' }
        }
      };
    }
    
    if (vehicleName.includes('ertiga')) {
      return {
        isSedan: false,
        showOnly8hr: false,
        packages: {
          '4hr': { hours: '4 Hrs', kms: '40 Kms', price: '₹2,100/-', extraKm: '17.00', extraHr: '170' },
          '8hr': { hours: '8 Hrs', kms: '80 Kms', price: '₹2,900/-', extraKm: '17.00', extraHr: '170' }
        }
      };
    }
    
    if (vehicleName.includes('fortuner') || vehicleName.includes('carnival')) {
      return {
        isSedan: false,
        showOnly8hr: true,
        packages: {
          '8hr': { hours: '8 Hrs', kms: '80 Kms', price: '₹7,000/-', extraKm: '60.00', extraHr: '600' }
        }
      };
    }
    
    if (vehicleName.includes('innova crysta')) {
      return {
        isSedan: false,
        showOnly8hr: true,
        packages: {
          '8hr': { hours: '8 Hrs', kms: '80 Kms', price: '₹3,500/-', extraKm: '22.00', extraHr: '220' }
        }
      };
    }
    
    if (vehicleName.includes('innova hycross') || vehicleName.includes('hycross')) {
      return {
        isSedan: false,
        showOnly8hr: true,
        packages: {
          '8hr': { hours: '8 Hrs', kms: '80 Kms', price: '₹3,800/-', extraKm: '24.00', extraHr: '250' }
        }
      };
    }

    if (vehicleName.includes('tempo')) {
      return {
        isSedan: false,
        showOnly8hr: true,
        packages: {
          '8hr': { hours: '8 Hrs', kms: '80 Kms', price: '₹5,500/-', extraKm: '30.00', extraHr: '400' }
        }
      };
    } 

    if (vehicleName.includes('bus')) {
      return {
        isSedan: false,
        showOnly8hr: true,
        packages: {
          '8hr': { hours: '8 Hrs', kms: '80 Kms', price: '₹10,000/-', extraKm: '50.00', extraHr: '600' }
        }
      };
    } 

    if (vehicleName.includes('urbania')) {
      return {
        isSedan: false,
        showOnly8hr: true,
        packages: {
          '8hr': { hours: '8 Hrs', kms: '80 Kms', price: '₹8,000/-', extraKm: '45.00', extraHr: '500' }
        }
      };
    } 

    const charges = getExtraCharges();
    const only8hr = showOnly8hr();
    return {
      isSedan: false,
      showOnly8hr: only8hr,
      packages: only8hr ? {
        '8hr': { hours: '8 Hrs', kms: '80 Kms', price: v.price8hr || '₹3,500/-', extraKm: charges.extraKm, extraHr: charges.extraHr }
      } : {
        '4hr': { hours: '4 Hrs', kms: '40 Kms', price: v.price || '₹1,000/-', extraKm: charges.extraKm, extraHr: charges.extraHr },
        '8hr': { hours: '8 Hrs', kms: '80 Kms', price: v.price8hr || '₹2,200/-', extraKm: charges.extraKm, extraHr: charges.extraHr }
      }
    };
  };

  const packageDetails = tripType === 'local' ? getPackageDetails() : null;
  const currentPackage = packageDetails?.packages[selectedPackage] || Object.values(packageDetails?.packages || {})[0];
  const packageBadgeLabel = currentPackage?.label || (currentPackage ? `${currentPackage.hours} / ${currentPackage.kms} Package` : '');
  const packageLineLabel = currentPackage?.label || (currentPackage ? `${currentPackage.hours} ${currentPackage.kms}` : '');
  const localDisplayName = getDisplayName();
  const airportBaseLabel = ['urbania', 'bus'].some((name) => (v.name || '').toLowerCase().includes(name))
    ? 'Base 8hr/80km'
    : 'Base (up to 40km)';
  const activeRateCard = v.selectedRateCard || {};
  const outstationMinKm = activeRateCard.maxKilometers || v.packageDetails?.kms || 300;
  const outstationDriverBata = activeRateCard.driverBata ?? v.packageDetails?.driverBata ?? 0;
  return (
    <div
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      style={{
        background: "#fff",
        borderRadius: 20,
        overflow: "hidden",
        marginBottom: 14,
        boxShadow: pressed ? "0 6px 12px rgba(0,0,0,0.1)" : "0 8px 20px rgba(0,0,0,0.08)",
        border: pressed ? `1.5px solid ${Y}` : "1.5px solid #F0F0F0",
        transform: pressed ? "scale(0.99)" : "scale(1)",
        transition: "all 0.2s ease",
        animation: `cardIn 0.4s ${index * 0.06}s ease both`,
      }}
    >
      {/* Image Section */}
      <div style={{
        position: "relative",
        height: "150px",
        background: "#f8f8f8",
        overflow: "hidden",
      }}>
        {!imageLoaded && (
          <div style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(90deg, #f0f0f0 0%, #f8f8f8 50%, #f0f0f0 100%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s infinite",
          }} />
        )}
        
        <img 
          src={imageUrl} 
          alt={v.name}
          onLoad={() => setImageLoaded(true)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            objectPosition: "center 60%",
            transform: "scale(1.1)",
            transition: "transform 0.5s ease, opacity 0.3s ease",
            opacity: imageLoaded ? 1 : 0,
          }}
        />

        <div style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "30%",
          background: "linear-gradient(to top, rgba(255,255,255,0.95), transparent)",
        }} />

        {bc && (
          <div style={{
            position: "absolute",
            top: 8,
            left: 8,
            background: bc.bg,
            borderRadius: 20,
            padding: "4px 10px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            zIndex: 2,
          }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: bc.color }}>
              {bc.label}
            </span>
          </div>
        )}

        <div style={{
          position: "absolute",
          top: 8,
          right: 8,
          background: "rgba(255,255,255,0.95)",
          borderRadius: 20,
          padding: "4px 10px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          backdropFilter: "blur(4px)",
          zIndex: 2,
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}>
          <MapPin size={10} color={YD} />
          <span style={{ fontSize: 9, fontWeight: 700, color: "#1A1A1A" }}>
            BLR
          </span>
        </div>
        
        {packageDetails?.showOnly8hr && (
          <div style={{
            position: "absolute",
            bottom: 8,
            left: 8,
            background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
            borderRadius: 20,
            padding: "4px 10px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            zIndex: 2,
          }}>
            <span style={{ fontSize: 9, fontWeight: 800, color: "#1A1A1A" }}>
              ⭐ PREMIUM • 8HR ONLY
            </span>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div style={{ padding: "12px" }}>
        
        {/* LOCAL FORMAT */}
        {tripType === 'local' && packageDetails && (
          <>
            <h3 style={{ fontSize: 18, fontWeight: 900, color: "#1A1A1A", margin: "0 0 8px 0", lineHeight: 1.2 }}>
              {localDisplayName}
            </h3>
            
            {!packageDetails.showOnly8hr && (
              <div style={{ display: "flex", gap: 8, marginBottom: 12, background: "#F0F0F0", padding: 4, borderRadius: 12 }}>
                <button onClick={() => setSelectedPackage('4hr')} style={{
                  flex: 1, border: "none", background: selectedPackage === '4hr' ? Y : "transparent",
                  color: selectedPackage === '4hr' ? "#1A1A1A" : "#666", padding: "8px 4px", borderRadius: 10,
                  fontSize: 11, fontWeight: 800, cursor: "pointer", transition: "all 0.2s ease",
                }}>4 Hrs / 40 Kms</button>
                <button onClick={() => setSelectedPackage('8hr')} style={{
                  flex: 1, border: "none", background: selectedPackage === '8hr' ? Y : "transparent",
                  color: selectedPackage === '8hr' ? "#1A1A1A" : "#666", padding: "8px 4px", borderRadius: 10,
                  fontSize: 11, fontWeight: 800, cursor: "pointer", transition: "all 0.2s ease",
                }}>8 Hrs / 80 Kms</button>
              </div>
            )}

            {packageDetails.showOnly8hr && (
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <div style={{ flex: 1, background: Y, color: "#1A1A1A", padding: "8px 4px", borderRadius: 10, fontSize: 11, fontWeight: 800, textAlign: "center" }}>
                  {packageBadgeLabel}
                </div>
              </div>
            )}
            
            <div style={{ background: "#F8F8F8", borderRadius: 12, padding: "12px", marginBottom: 10, border: "1px solid #EEEEEE" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1A1A1A", marginBottom: 8 }}>
                Package {packageLineLabel}
              </div>
              <div style={{ fontSize: 13, color: "#1A1A1A", marginBottom: 8 }}>
                Extra KM ₹{currentPackage.extraKm} • Extra HR ₹{currentPackage.extraHr}
              </div>
              <div style={{ fontSize: 13, color: "#1A1A1A", marginBottom: 10 }}>
                {v.seaters}+1 people • {v.bags} luggage
              </div>
              <div style={{ fontSize: 13, color: "#1A1A1A", marginBottom: 8 }}>
                Toll & Parking Extra
              </div>
              <div style={{ fontSize: 18, fontWeight: 900, color: "#1A1A1A", borderTop: "1px dashed #DDD", paddingTop: 8 }}>
                {currentPackage.price}
              </div>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
              {v.features?.map((f, fi) => (
                <span key={fi} style={{ fontSize: 9, fontWeight: 600, color: YD, background: YL, padding: "3px 8px", borderRadius: 20, border: `1px solid #FFE082` }}>
                  {f}
                </span>
              ))}
            </div>
          </>
        )}
        
        {/* AIRPORT FORMAT */}
        {tripType === 'airport' && (
          <>
            <div style={{ marginBottom: 8 }}>
              <h3 style={{ fontSize: 18, fontWeight: 900, color: "#1A1A1A", margin: 0, lineHeight: 1.2 }}>
                {v.name}
              </h3>
            </div>

            <div style={{ background: "#F8F8F8", borderRadius: 12, padding: "12px", marginBottom: 10, border: "1px solid #EEEEEE" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, paddingBottom: 8, borderBottom: "1px dashed #DDD" }}>
                <span style={{ fontSize: 13, color: "#666", fontWeight: 500 }}>{airportBaseLabel}</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: "#1A1A1A" }}>{v.price}</span>
              </div>
              
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, paddingBottom: 8, borderBottom: "1px dashed #DDD" }}>
                <span style={{ fontSize: 13, color: "#666", fontWeight: 500 }}>Capacity</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: "#1A1A1A" }}>{v.seaters} seats • {v.bags} bags</span>
              </div>
              
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, paddingBottom: 8, borderBottom: "1px dashed #DDD" }}>
                <span style={{ fontSize: 13, color: "#666", fontWeight: 500 }}>Toll Charges</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: "#E5182E" }}>Extra</span>
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 18, fontWeight: 900, color: "#1A1A1A" }}>{v.price}</span>
                {v.rating && (
                  <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Star size={14} color={Y} fill={Y} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#1A1A1A" }}>{v.rating}</span>
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
              {v.features?.map((f, fi) => (
                <span key={fi} style={{ fontSize: 9, fontWeight: 600, color: YD, background: YL, padding: "3px 8px", borderRadius: 20, border: `1px solid #FFE082` }}>
                  {f}
                </span>
              ))}
            </div>
          </>
        )}
        
        {/* OUTSTATION FORMAT */}
        {tripType === 'outstation' && (
          <>
            <div style={{ marginBottom: 8 }}>
              <h3 style={{ fontSize: 18, fontWeight: 900, color: "#1A1A1A", margin: 0, lineHeight: 1.2 }}>
                {v.name}
              </h3>
            </div>

            <div style={{ background: "#F8F8F8", borderRadius: 12, padding: "12px", marginBottom: 10, border: "1px solid #EEEEEE" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, paddingBottom: 8, borderBottom: "1px dashed #DDD" }}>
                <span style={{ fontSize: 13, color: "#666", fontWeight: 500 }}>Min/day</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: "#1A1A1A" }}>{outstationMinKm} km</span>
              </div>
              
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, paddingBottom: 8, borderBottom: "1px dashed #DDD" }}>
                <span style={{ fontSize: 13, color: "#666", fontWeight: 500 }}>Driver Bata</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: "#1A1A1A" }}>₹{outstationDriverBata}/day</span>
              </div>
              
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, paddingBottom: 8, borderBottom: "1px dashed #DDD" }}>
                <span style={{ fontSize: 13, color: "#666", fontWeight: 500 }}>Capacity</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: "#1A1A1A" }}>{v.seaters}+1 • {v.bags} bags</span>
              </div>
              
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 13, color: "#666", fontWeight: 500 }}>Toll, Parking & State Permit</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: "#1A1A1A" }}>Extra</span>
              </div>
            </div>

            <div style={{ fontSize: 18, fontWeight: 900, color: "#1A1A1A", textAlign: 'left', marginBottom: 10 }}>
              {v.price}
            </div>
          </>
        )}

        {/* Action Buttons - Only WhatsApp and Book Now */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
          marginTop: 4,
        }}>
          <button
            onClick={(e) => { e.stopPropagation(); onBook({...v, selectedPackage, packageDetails: currentPackage}); }}
            style={{
              background: Y,
              border: "none",
              color: "#1A1A1A",
              padding: "10px 0",
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 800,
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
          >
            Book Now
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); onWhatsApp(v); }}
            style={{
              background: "#25D366",
              border: "none",
              color: "#fff",
              padding: "10px 0",
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 800,
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
          >
            WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
}

// Main Component
export default function TopRatedRides({
  initialTrip = "airport",
  lockTrip = false,
  heading = "Discover Plus cabs Top-Rated Rides",
  subtitle = "Explore Our Most Popular Car Rental Options Trusted by thousands across India for flexible short-term and long-term car rentals.",
}) {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(initialTrip);
  const [vehiclesByTrip, setVehiclesByTrip] = useState({ airport: [], outstation: [], local: [] });
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(true);
  const [vehicleLoadError, setVehicleLoadError] = useState('');
  const canSwitchTrip = !lockTrip;


  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    setSelectedTrip(initialTrip);
  }, [initialTrip]);

  useEffect(() => {
    let isMounted = true;

    const loadVehicles = async () => {
      try {
        setIsLoadingVehicles(true);
        setVehicleLoadError('');
        const vehicles = await fetchPublicVehicles();
        if (isMounted) {
          setVehiclesByTrip(groupVehiclesByTrip(vehicles));
        }
      } catch (error) {
        if (isMounted) {
          setVehicleLoadError(error.message || 'Unable to load vehicles');
          setVehiclesByTrip({ airport: [], outstation: [], local: [] });
        }
      } finally {
        if (isMounted) {
          setIsLoadingVehicles(false);
        }
      }
    };

    loadVehicles();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleBookNow = (vehicle) => {
    const tripLabel = toBookingTripLabel(selectedTrip);
    const vehicleData = {
      ...vehicle,
      category: vehicle.category || vehicle.type || selectedTrip,
    };
    const bookingData = buildBookingDataFromVehicle(vehicleData, selectedTrip);

    navigate('/booking', {
      state: {
        selectedVehicleData: vehicleData,
        initialRouteData: bookingData,
        tripType: tripLabel,
      },
    });
  };

  const handleWhatsApp = (vehicle) => {
    const phoneNumber = "917349519354";
    const message = `Hello! I'm interested in renting the ${vehicle.name} for ${selectedTrip || 'my trip'}.`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
  };

  const activeTrip = TRIPS.find(t => t.id === selectedTrip);

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&family=Barlow:wght@400;600;700;800&display=swap');
    *{box-sizing:border-box;}
    ::-webkit-scrollbar{display:none;}
    @keyframes fadeIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
    @keyframes cardIn{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
    @keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
    @keyframes slideUp{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:translateY(0)}}
    @keyframes popIn{0%{transform:scale(0.8);opacity:0}100%{transform:scale(1);opacity:1}}
  `;

  // Mobile View
  if (isMobile) {
    return (
      <div style={{ minHeight:"100vh", width:"100%", maxWidth:"100vw", background:"#F5F5F5", fontFamily:font, overflowX:"hidden", padding: "20px 16px 60px" }}>
        <style>{css}</style>

        {/* Header */}
        <div style={{ marginBottom: 20, maxWidth:"100%", overflow:"hidden" }}>
          <h2 style={{ fontSize: 24, fontWeight: 900, color: "#1A1A1A", margin: "0 0 8px" }}>
            {heading}
          </h2>
          <p style={{ fontSize: 13, color: "#666", margin: 0 }}>
            {subtitle}
          </p>
        </div>

        {/* Trip Type Selector */}
{/* Trip Selection - One Line */}
{canSwitchTrip && (
<div style={{ marginBottom: 22, maxWidth:"100%", overflow:"hidden" }}>

  {/* Header */}
  {/* <div style={{
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 12
  }}>
    <div style={{
      width: 24,
      height: 24,
      borderRadius: 8,
      background: "#f88e0c",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 12,
      fontWeight: 800,
      color: "#fff"
    }}>
      1
    </div>
    <span style={{
      fontSize: 13,
      fontWeight: 800,
      color: "#222"
    }}>
      Choose Trip
    </span>
  </div> */}

  {/* One Line 3 Items */}
  <div style={{
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 10,
    maxWidth: "100%",
    overflow: "hidden"
  }}>
    {TRIPS.map((trip) => {
      const isActive = selectedTrip === trip.id;

      return (
        <div
          key={trip.id}
          onClick={() => setSelectedTrip(trip.id)}
          style={{
            cursor: "pointer",
            padding: "12px 6px",
            borderRadius: 18,
            textAlign: "center",
            background: isActive
              ? "linear-gradient(135deg,#f88e0c,#ffb347)"
              : "#ffffff",
            border: isActive
              ? "none"
              : "1px solid #e5e5e5",
            boxShadow: isActive
              ? "0 6px 14px rgba(248,142,12,0.25)"
              : "0 3px 8px rgba(0,0,0,0.06)",
            transition: "all 0.25s ease",
            minWidth: 0
          }}
        >

          {/* Icon */}
          <div style={{
            fontSize: 18,
            marginBottom: 4
          }}>
            {trip.icon}
          </div>

          {/* Label */}
          <div style={{
            fontSize: 12,
            fontWeight: 800,
            color: isActive ? "#fff" : "#333"
          }}>
            {trip.label}
          </div>

        </div>
      );
    })}
  </div>
</div>
)}



        {/* Vehicles */}
        <div>
          {!selectedTrip ? (
            <div style={{ animation: "fadeIn 0.4s ease" }}>
              <div style={{ background: "#fff", borderRadius: 24, padding: "32px 20px", textAlign: "center", boxShadow: "0 4px 20px rgba(0,0,0,0.05)", border: "2px dashed #FFE082", maxWidth:"100%", overflow:"hidden" }}>
                <p style={{ fontSize: 18, fontWeight: 900, color: "#1A1A1A", margin: "0 0 8px" }}>Select a trip type</p>
                <p style={{ fontSize: 13, color: "#BBB", fontWeight: 700, margin: "0 0 20px" }}>
                  Choose airport, outstation, or local to see available vehicles
                </p>
                <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap:"wrap", maxWidth:"100%" }}>
                  {TRIPS.map(t => (
                    <button key={t.id} onClick={() => setSelectedTrip(t.id)} style={{
                      background: Y, border: "none", borderRadius: 14, padding: "8px 14px", cursor: "pointer",
                      fontSize: 11, fontWeight: 900, color: "#1A1A1A", display: "flex", alignItems: "center", gap: 4,
                    }}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ animation: "fadeIn 0.3s ease", maxWidth:"100%", overflow:"hidden" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 12 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                    <div style={{ width: 22, height: 22, borderRadius: 8, background: Y, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900, color: "#1A1A1A" }}>
                      {canSwitchTrip ? 2 : 1}
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 900, color: "#555", letterSpacing: 0.5 }}>
                      Select vehicle
                    </span>
                  </div>
                  <span style={{ fontSize: 18, fontWeight: 900, color: "#1A1A1A" }}>
                    {vehiclesByTrip[selectedTrip]?.length || 0} Available
                  </span>
                </div>
                {canSwitchTrip && (
                <button onClick={() => setSelectedTrip(null)} style={{
                  background: "#fff", border: `1.5px solid #E8E8E8`, color: "#555", padding: "8px 16px",
                  borderRadius: 30, fontSize: 11, fontWeight: 800, cursor: "pointer",
                }}>
                  ← Back
                </button>
                )}
              </div>

              {isLoadingVehicles && (
                <div style={{ background: '#fff', borderRadius: 18, padding: 24, textAlign: 'center', color: '#666', fontWeight: 800 }}>
                  Loading vehicles...
                </div>
              )}
              {!isLoadingVehicles && vehicleLoadError && (
                <div style={{ background: '#fff', borderRadius: 18, padding: 24, textAlign: 'center', color: '#666', fontWeight: 800 }}>
                  {vehicleLoadError}
                </div>
              )}
              {!isLoadingVehicles && !vehicleLoadError && vehiclesByTrip[selectedTrip]?.map((v, i) => (
                <MobileVCard
                  key={v.id}
                  v={v}
                  index={i}
                  tripType={selectedTrip}
                  onBook={handleBookNow}
                  onWhatsApp={handleWhatsApp}
                />
              ))}
            </div>
          )}
        </div>

      </div>
    );
  }

  // Desktop View
  return (
    <div style={{ background: "#ECEEF5", minHeight: "100vh", padding: "40px 16px 60px", fontFamily: fontD }}>
      <style>{css}</style>
      
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <h2 style={{ fontSize: "clamp(20px, 5vw, 34px)", fontWeight: 800, color: "#000000", letterSpacing: "-0.5px", marginBottom: 8 }}>
            {heading}
          </h2>
          <p style={{ fontSize: "clamp(12px, 2vw, 14px)", color: "#6B7280", maxWidth: 560, margin: "0 auto", lineHeight: 1.5 }}>
            {subtitle}
          </p>
        </div>

        {/* Trip Type Selector for Desktop */}
        {canSwitchTrip && (
        <div style={{ display: "flex", justifyContent: "center", gap: 10, marginBottom: 30, flexWrap: "wrap" }}>
          {/* <button
            onClick={() => setSelectedTrip(null)}
            style={{
              padding: "10px 24px", border: "none", borderRadius: 30, fontWeight: 600, fontSize: 14,
              cursor: "pointer", transition: "all 0.3s ease",
              background: !selectedTrip ? "#4A6CF7" : "white",
              color: !selectedTrip ? "white" : "#666",
              boxShadow: !selectedTrip ? "0 4px 14px rgba(74,108,247,0.35)" : "none"
            }}
          >
            All Cars
          </button> */}
          {TRIPS.map(trip => (
            <button
              key={trip.id}
              onClick={() => setSelectedTrip(trip.id)}
              style={{
                padding: "10px 24px", border: "none", borderRadius: 30, fontWeight: 600, fontSize: 14,
                cursor: "pointer", transition: "all 0.3s ease",
                background: selectedTrip === trip.id ? trip.color : "white",
                color: selectedTrip === trip.id ? "white" : "#666",
                boxShadow: selectedTrip === trip.id ? `0 4px 14px ${trip.color}35` : "none"
              }}
            >
              {trip.label}
            </button>
          ))}
        </div>
        )}

        {/* Vehicle Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
          {isLoadingVehicles && (
            <div style={{ gridColumn: '1 / -1', background: '#fff', borderRadius: 18, padding: 24, textAlign: 'center', color: '#666', fontWeight: 800 }}>
              Loading vehicles...
            </div>
          )}
          {!isLoadingVehicles && vehicleLoadError && (
            <div style={{ gridColumn: '1 / -1', background: '#fff', borderRadius: 18, padding: 24, textAlign: 'center', color: '#666', fontWeight: 800 }}>
              {vehicleLoadError}
            </div>
          )}
          {!isLoadingVehicles && !vehicleLoadError && (selectedTrip ? vehiclesByTrip[selectedTrip] : Object.values(vehiclesByTrip).flat()).map((v, i) => (
            <MobileVCard
              key={v.id}
              v={v}
              index={i}
              tripType={selectedTrip || 'outstation'}
              onBook={handleBookNow}
              onWhatsApp={handleWhatsApp}
            />
          ))}
        </div>

        {selectedTrip && !isLoadingVehicles && !vehicleLoadError && vehiclesByTrip[selectedTrip]?.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            No vehicles found for this category.
          </div>
        )}
      </div>

    </div>
  );
}
